import { useEffect, useRef } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { describeStrings } from '../utils/describe.strings';

type QuestionKind = 'core' | 'follow-up';

interface QuestionShellProps {
  kind: QuestionKind;
  stepNumber?: number;
  heading: string;
  subheading?: string;
  status: 'active' | 'answered' | 'upcoming';
  isLast: boolean;
  children: React.ReactNode;
}

export const QuestionShell = ({
  kind,
  stepNumber,
  heading,
  subheading,
  status,
  isLast,
  children,
}: QuestionShellProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === 'active' && sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [status]);

  if (status === 'upcoming') return null;

  const isActive = status === 'active';
  const isAnswered = status === 'answered';

  return (
    <div
      ref={sectionRef}
      className={cn(
        'relative z-10 flex gap-6 transition-opacity duration-300 animate-fade-in',
        isAnswered && 'opacity-60 hover:opacity-100',
        isActive && 'py-2'
      )}
      aria-current={isActive ? 'step' : undefined}
    >
      {!isLast && (
        <div
          className="absolute start-[22px] top-[22px] -bottom-8 w-px bg-slate-200"
          aria-hidden
        />
      )}
      <div className="shrink-0">
        <QuestionMarker
          kind={kind}
          stepNumber={stepNumber}
          isActive={isActive}
        />
      </div>
      <div className="flex-1 pt-1.5 min-w-0">
        {kind === 'follow-up' && (
          <div className="flex items-center gap-1.5 mb-1 text-[11px] font-semibold uppercase tracking-wider text-blue-600">
            <Sparkles className="h-3 w-3" aria-hidden />
            <span>{describeStrings.followUps.label}</span>
          </div>
        )}
        <h3
          className={cn('mb-2', {
            'text-2xl font-semibold tracking-tight text-slate-900':
              kind === 'core' && isActive,
            'text-sm font-medium text-slate-500': kind === 'core' && !isActive,
            'text-base font-medium text-slate-800': kind === 'follow-up',
          })}
        >
          {heading}
        </h3>
        {subheading && isActive && (
          <p className="text-slate-500 text-sm mb-6">{subheading}</p>
        )}
        <div className={isAnswered ? 'pointer-events-none select-none' : ''}>
          {children}
        </div>
      </div>
    </div>
  );
};

interface QuestionMarkerProps {
  kind: QuestionKind;
  stepNumber?: number;
  isActive: boolean;
}

function QuestionMarker({ kind, stepNumber, isActive }: QuestionMarkerProps) {
  if (kind === 'follow-up') {
    return (
      <div
        className={cn(
          'w-11 h-11 rounded-full flex items-center justify-center border',
          isActive
            ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-[0_0_0_4px_rgba(0,115,234,0.12)]'
            : 'bg-slate-100 border-slate-200 text-slate-400'
        )}
      >
        <Sparkles className="h-4 w-4" aria-hidden />
      </div>
    );
  }

  if (isActive) {
    return (
      <div className="w-11 h-11 rounded-full bg-primary text-white font-medium flex items-center justify-center shadow-[0_0_0_4px_rgba(0,115,234,0.15)]">
        {stepNumber}
      </div>
    );
  }

  return (
    <div className="w-11 h-11 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
      <Check className="h-4 w-4" aria-hidden />
    </div>
  );
}
